/* ============================================================================
   MartheGenerate.tsx — saisie de scène + génération d'image via le provider
   sélectionné (Gemini Nano Banana ou OpenAI gpt-image-1).
   Visible dans la sidebar gauche uniquement quand `params.mode === 'marthe'`.

   Pipeline :
     1. user choisit provider (gemini | openai) + tape une scène (max 200 chars)
     2. clic "✨ générer" → POST /api/generate avec { scene, turnstileToken, provider }
     3. worker → provider → R2 cache (séparé par provider) → renvoie PNG bytes
     4. côté client : bytes → data URL base64 → dispatch imageSrc
     5. CreationCanvas voit imageSrc + mode='marthe' → utilise
        imageToAsciiGrid avec glyphSet 'fairy'

   Esthétique : terminal mono sobre, spinner ASCII, bouton invert hover.
   Le provider est une *préférence UI locale* — pas dans le canvasStore (le
   store ne porte que des params de rendu, pas la config réseau).
   ============================================================================ */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useCanvasParams } from '../../lib/canvasStore';
import { apiClient, ApiClientError } from '../../lib/api';
import { RadioGroup, type RadioOption } from './RadioGroup';

// Window.turnstile typing déclaré une seule fois dans Send.tsx (page /send)
// pour éviter "Subsequent property declarations must have the same type". On
// se contente de l'utiliser via un type local pour les méthodes nécessaires.
declare global {
  interface Window {
    onloadMartheTurnstileCallback?: () => void;
  }
}

const MAX_SCENE = 200;
const PLACEHOLDER = 'fée tisseuse dans une maison fragmentée…';

const TURNSTILE_SITE_KEY =
  (import.meta as { env?: Record<string, string | undefined> }).env
    ?.VITE_TURNSTILE_SITE_KEY ?? '1x00000000000000000000AA';

/**
 * Test sitekeys Cloudflare commencent par `1x` ou `2x` → on n'affiche pas
 * le widget (UI propre). Le worker fait également un bypass sur les secret
 * keys de test, donc la validation côté serveur passe sans token réel.
 */
const IS_TEST_SITEKEY =
  TURNSTILE_SITE_KEY.startsWith('1x') || TURNSTILE_SITE_KEY.startsWith('2x');

type Provider = 'gemini' | 'openai';

const PROVIDER_OPTIONS: RadioOption<Provider>[] = [
  { value: 'gemini', label: 'gemini', title: 'Gemini 2.5 Flash Image (Nano Banana)' },
  { value: 'openai', label: 'openai', title: 'OpenAI gpt-image-1' },
];

/* ----------------------------------------------------------------------------
   Spinner ASCII — un tick toutes les 80ms, mono uppercase
   ---------------------------------------------------------------------------- */

const SPINNER_FRAMES = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];

function useSpinner(active: boolean): string {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setFrame((f) => (f + 1) % SPINNER_FRAMES.length), 80);
    return () => clearInterval(id);
  }, [active]);
  return SPINNER_FRAMES[frame] ?? SPINNER_FRAMES[0]!;
}

/* ----------------------------------------------------------------------------
   Composant
   ---------------------------------------------------------------------------- */

type Status = 'idle' | 'submitting' | 'error';

const apiKeyStorageKey = (p: Provider) => `marthe_apikey_${p}`;

function loadApiKey(p: Provider): string {
  try {
    return localStorage.getItem(apiKeyStorageKey(p)) ?? '';
  } catch {
    return '';
  }
}

function saveApiKey(p: Provider, key: string): void {
  try {
    if (key.trim().length === 0) localStorage.removeItem(apiKeyStorageKey(p));
    else localStorage.setItem(apiKeyStorageKey(p), key.trim());
  } catch {
    // ignore (private mode, etc)
  }
}

export function MartheGenerate() {
  const [, dispatch] = useCanvasParams();
  const [provider, setProvider] = useState<Provider>('gemini');
  const [scene, setScene] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  // BYOK : clé locale par provider, persistée en localStorage. Jamais
  // envoyée au serveur sauf au moment du submit (one-shot, pas loggée worker).
  const [apiKey, setApiKey] = useState<string>(() => loadApiKey('gemini'));
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [apiKeySaved, setApiKeySaved] = useState<boolean>(
    () => loadApiKey('gemini').length > 0,
  );
  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  // Reload key from localStorage when provider switches
  useEffect(() => {
    const stored = loadApiKey(provider);
    setApiKey(stored);
    setApiKeySaved(stored.length > 0);
  }, [provider]);

  const onSaveKey = useCallback(() => {
    saveApiKey(provider, apiKey);
    setApiKeySaved(apiKey.trim().length > 0);
  }, [provider, apiKey]);

  const onClearKey = useCallback(() => {
    saveApiKey(provider, '');
    setApiKey('');
    setApiKeySaved(false);
  }, [provider]);

  // Mount Turnstile widget invisible
  useEffect(() => {
    // En mode test (sitekey 1x/2x), on skip le render — le worker bypass de
    // toute façon. Token "test" pour passer le check zod côté worker (min 1).
    if (IS_TEST_SITEKEY) {
      setTurnstileToken('test-bypass');
      return;
    }
    const renderWidget = () => {
      if (!window.turnstile || !turnstileRef.current) return;
      // Si déjà mount (re-render), reset et reuse
      if (turnstileWidgetIdRef.current) {
        try {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        } catch {
          // ignore
        }
        return;
      }
      try {
        const id = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (token: string) => setTurnstileToken(token),
          theme: 'light',
          size: 'invisible',
        });
        turnstileWidgetIdRef.current = id;
      } catch {
        // ignore — environnements bloqués (eg test)
      }
    };

    if (window.turnstile) {
      renderWidget();
      return;
    }

    // Si script déjà chargé par un autre composant (ex /send), utiliser même path
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-turnstile-loader="1"]',
    );
    if (existing) {
      const onLoad = () => renderWidget();
      existing.addEventListener('load', onLoad, { once: true });
      // si déjà loaded
      if (window.turnstile) renderWidget();
      return () => existing.removeEventListener('load', onLoad);
    }

    const script = document.createElement('script');
    script.src =
      'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onloadMartheTurnstileCallback';
    script.async = true;
    script.defer = true;
    script.dataset.turnstileLoader = '1';
    window.onloadMartheTurnstileCallback = renderWidget;
    document.head.appendChild(script);

    return () => {
      // pas de removal du script (peut être réutilisé), juste nettoyer le hook
      window.onloadMartheTurnstileCallback = undefined;
    };
  }, []);

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = scene.trim();
      if (!trimmed || trimmed.length > MAX_SCENE || status === 'submitting') return;

      setStatus('submitting');
      setError(null);
      try {
        // Si l'user a sauvegardé une clé pour ce provider, on l'envoie au worker
        // (override la clé serveur). Sinon undefined → worker fallback à env.
        const userKey = apiKeySaved && apiKey.trim().length > 0 ? apiKey.trim() : undefined;
        const dataUrl = await apiClient.generateScene(
          trimmed,
          turnstileToken,
          provider,
          userKey,
        );
        dispatch({ type: 'set', key: 'imageSrc', value: dataUrl });
        setStatus('idle');
        // Reset turnstile pour la prochaine génération
        if (window.turnstile && turnstileWidgetIdRef.current) {
          try {
            window.turnstile.reset(turnstileWidgetIdRef.current);
          } catch {
            // ignore
          }
          setTurnstileToken('');
        }
      } catch (err) {
        let message: string;
        if (err instanceof ApiClientError) {
          // 503 PROVIDER_NOT_CONFIGURED → message contextualisé
          if (err.code === 'PROVIDER_NOT_CONFIGURED') {
            message = `provider ${provider} indisponible — switch à l'autre`;
          } else if (
            err.status === 401 || err.status === 403
            || /API key not valid|invalid_api_key|Incorrect API key|API_KEY_INVALID/i.test(err.message)
          ) {
            // Clé API rejetée. Causes Gemini fréquentes (ordre de probabilité) :
            //  1. CARTE BANCAIRE EXPIRÉE sur le compte facturation Gemini —
            //     Google retourne "API key not valid" trompeur quand le
            //     payment method n'est plus valide pour les modèles payants
            //     (image gen Nano Banana = $0.04/req, nécessite billing).
            //     Fix : console.cloud.google.com/billing → mettre à jour carte
            //  2. Restrictions IP sur la clé (HTTP referrers / IP allowlist) →
            //     bloque les appels depuis IP Cloudflare Workers
            //     Fix : console.cloud.google.com/apis/credentials → clé → None
            //  3. Generative Language API pas activée sur le projet
            //     Fix : recréer via aistudio.google.com (active l'API auto)
            message = provider === 'gemini'
              ? 'Gemini rejette les requêtes. Causes fréquentes : 1) Carte bancaire expirée sur ton compte facturation Gemini (Google renvoie le même message pour billing KO). Vérifie console.cloud.google.com/billing. 2) Restrictions IP sur ta clé → console.cloud.google.com/apis/credentials → ta clé → Application restrictions = None. 3) Sinon recréer via aistudio.google.com/apikey.'
              : 'clé OpenAI invalide. obtiens une clé sur https://platform.openai.com/api-keys (format sk-...) et colle-la ci-dessus';
          } else {
            message = err.message;
          }
        } else if (err instanceof Error) {
          message = err.message;
        } else {
          message = 'erreur réseau';
        }
        setError(message);
        setStatus('error');
      }
    },
    [scene, status, turnstileToken, dispatch, provider, apiKey, apiKeySaved],
  );

  const spinner = useSpinner(status === 'submitting');
  const trimmedLen = scene.trim().length;
  const tooLong = scene.length > MAX_SCENE;
  const canSubmit = trimmedLen > 0 && !tooLong && status !== 'submitting';

  return (
    <form className="marthe-generate" onSubmit={onSubmit} noValidate>
      <p className="marthe-generate__subtitle">
        décris ta fée → image générée → glyph art
      </p>

      {/* Provider toggle — discret, mono, au-dessus de la textarea */}
      <div className="marthe-generate__provider" data-testid="marthe-provider">
        <div className="controls-sublabel">provider</div>
        <RadioGroup
          options={PROVIDER_OPTIONS}
          value={provider}
          onChange={(v) => setProvider(v)}
        />
      </div>

      {/* BYOK — clé API locale par provider. Stockée localStorage. */}
      <div className="marthe-generate__apikey" data-testid="marthe-apikey">
        <div className="controls-sublabel">
          api key
          {apiKeySaved && (
            <span className="marthe-generate__apikey-status">
              ● saved locally
            </span>
          )}
          {!apiKeySaved && (
            <span className="marthe-generate__apikey-status marthe-generate__apikey-status--off">
              ○ using server key
            </span>
          )}
        </div>
        <div className="marthe-generate__apikey-row">
          <input
            type={apiKeyVisible ? 'text' : 'password'}
            className="marthe-generate__apikey-input"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={provider === 'gemini' ? 'AIza...' : 'sk-...'}
            spellCheck={false}
            autoComplete="off"
            data-testid="marthe-apikey-input"
            aria-label={`clé api ${provider}`}
          />
          <button
            type="button"
            className="marthe-generate__apikey-toggle"
            onClick={() => setApiKeyVisible((v) => !v)}
            title={apiKeyVisible ? 'masquer' : 'afficher'}
            aria-label={apiKeyVisible ? 'masquer la clé' : 'afficher la clé'}
          >
            {apiKeyVisible ? '◐' : '◑'}
          </button>
        </div>
        <div className="marthe-generate__apikey-actions">
          <button
            type="button"
            className="marthe-generate__apikey-btn"
            onClick={onSaveKey}
            disabled={apiKey.trim().length < 20}
            data-testid="marthe-apikey-save"
            title="enregistrer dans ce navigateur"
          >
            save
          </button>
          <button
            type="button"
            className="marthe-generate__apikey-btn"
            onClick={onClearKey}
            disabled={!apiKeySaved}
            data-testid="marthe-apikey-clear"
            title="effacer la clé locale"
          >
            clear
          </button>
        </div>
        <p className="marthe-generate__apikey-note">
          stockée dans ton navigateur uniquement, jamais sauvegardée serveur
        </p>
      </div>

      <textarea
        className="marthe-generate__textarea"
        value={scene}
        onChange={(e) => setScene(e.target.value)}
        placeholder={PLACEHOLDER}
        maxLength={MAX_SCENE + 50 /* soft cap, on coupe affichage */}
        rows={3}
        spellCheck={false}
        autoComplete="off"
        disabled={status === 'submitting'}
        aria-label="scène à générer"
      />

      <div className="marthe-generate__counter">
        <span className={tooLong ? 'marthe-generate__counter--err' : ''}>
          {Math.min(scene.length, MAX_SCENE * 2)}/{MAX_SCENE}
        </span>
      </div>

      {/* Honeypot — caché aux humains, visible aux bots */}
      <div className="marthe-generate__honeypot" aria-hidden="true">
        <label htmlFor="marthe-website">website</label>
        <input
          id="marthe-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      {/* Turnstile invisible widget */}
      <div ref={turnstileRef} data-testid="marthe-turnstile-mount" />

      <button
        type="submit"
        className="marthe-generate__btn"
        disabled={!canSubmit}
        data-testid="marthe-generate-button"
      >
        {status === 'submitting' ? (
          <span>
            <span className="marthe-generate__spinner">{spinner}</span>{' '}
            génération via {provider}…
          </span>
        ) : (
          <span>✨ générer</span>
        )}
      </button>

      {error && (
        <p className="marthe-generate__error" role="alert">
          × {error}
        </p>
      )}
    </form>
  );
}
